/**
 * Locale tables for the two small advanced-rail surfaces — Map and Code.
 *
 * Kept together because neither is large enough to earn its own module and
 * both are chrome around a foreign panel (`JsonEditorPanel` for Code, the page
 * list for Map). Same co-located `Record<LocaleCode, …>` pattern the rest of
 * `shell/ide-v2` uses.
 *
 * NOT LOCALIZED, deliberately: route strings and page ids (project payload —
 * whatever the operator named their own pages) and the `.cogo-studio.json`
 * download extension (a file format token).
 */
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface RailSurfaceCopy {
  map: {
    eyebrow: string;
    title: string;
    description: string;
    reviewSiteMap: string;
    activePage: string;
    emptyTitle: string;
    emptyBody: string;
    untitledPage: string;
    /** "3 blocks" — the count sits inside the phrase, not before it. */
    blockCount: (count: number) => string;
    openPage: (page: string) => string;
  };
  code: {
    enableAllTools: string;
    dismiss: string;
    bundleTitle: string;
    bundleDescription: string;
    reviewBundle: string;
    exportBundle: string;
    exportAction: string;
    importBundle: string;
    importAction: string;
    /** Toasts — imperative messages, outside any rendered-surface gate. */
    exported: string;
    exportFailed: (reason: string) => string;
    imported: (name: string) => string;
    importFailed: (reason: string) => string;
    unknownError: string;
  };
}

const en: RailSurfaceCopy = {
  map: {
    eyebrow: "§ map · site structure",
    title: "Site map",
    description:
      "Review page order, routes, and block coverage for this project.",
    reviewSiteMap: "Review site map",
    activePage: "Active page",
    emptyTitle: "No pages defined yet",
    emptyBody:
      "Describe your app in the chat or add a page from the Pages panel.",
    untitledPage: "Untitled page",
    blockCount: (count) => `${count} blocks`,
    openPage: (page) => `Open ${page} in Page`,
  },
  code: {
    enableAllTools: "Enable All tools",
    dismiss: "Dismiss",
    bundleTitle: "Project bundle",
    bundleDescription: "Export or import full Coogo Studio project JSON.",
    reviewBundle: "Review bundle",
    exportBundle: "Export Coogo bundle",
    exportAction: "Export",
    importBundle: "Import Coogo bundle",
    importAction: "Import",
    exported: "Coogo bundle exported",
    exportFailed: (reason) => `Export failed: ${reason}`,
    imported: (name) => `Imported ${name}`,
    importFailed: (reason) => `Import failed: ${reason}`,
    unknownError: "Unknown error",
  },
};

const ko: RailSurfaceCopy = {
  map: {
    eyebrow: "§ 맵 · 사이트 구조",
    title: "사이트 맵",
    description: "이 프로젝트의 페이지 순서, 경로, 블록 구성을 확인하세요.",
    reviewSiteMap: "사이트 맵 검토",
    activePage: "현재 페이지",
    emptyTitle: "아직 정의된 페이지가 없습니다",
    emptyBody: "채팅에서 앱을 설명하거나 페이지 패널에서 페이지를 추가하세요.",
    untitledPage: "제목 없는 페이지",
    blockCount: (count) => `블록 ${count}개`,
    openPage: (page) => `${page} 페이지 열기`,
  },
  code: {
    enableAllTools: "모든 도구 켜기",
    dismiss: "닫기",
    bundleTitle: "프로젝트 번들",
    bundleDescription:
      "Coogo Studio 프로젝트 JSON 전체를 내보내거나 가져옵니다.",
    reviewBundle: "번들 검토",
    exportBundle: "Coogo 번들 내보내기",
    exportAction: "내보내기",
    importBundle: "Coogo 번들 가져오기",
    importAction: "가져오기",
    exported: "Coogo 번들을 내보냈습니다",
    exportFailed: (reason) => `내보내기 실패: ${reason}`,
    imported: (name) => `${name}을(를) 가져왔습니다`,
    importFailed: (reason) => `가져오기 실패: ${reason}`,
    unknownError: "알 수 없는 오류",
  },
};

const ja: RailSurfaceCopy = {
  map: {
    eyebrow: "§ マップ · サイト構造",
    title: "サイトマップ",
    description:
      "このプロジェクトのページ順、ルート、ブロックの構成を確認します。",
    reviewSiteMap: "サイトマップを確認",
    activePage: "現在のページ",
    emptyTitle: "まだページが定義されていません",
    emptyBody:
      "チャットでアプリを説明するか、ページパネルからページを追加してください。",
    untitledPage: "無題のページ",
    blockCount: (count) => `ブロック ${count} 件`,
    openPage: (page) => `${page} をページで開く`,
  },
  code: {
    enableAllTools: "すべてのツールを有効化",
    dismiss: "閉じる",
    bundleTitle: "プロジェクトバンドル",
    bundleDescription:
      "Coogo Studio プロジェクト JSON 全体を書き出し・読み込みします。",
    reviewBundle: "バンドルを確認",
    exportBundle: "Coogo バンドルを書き出す",
    exportAction: "書き出し",
    importBundle: "Coogo バンドルを読み込む",
    importAction: "読み込み",
    exported: "Coogo バンドルを書き出しました",
    exportFailed: (reason) => `書き出しに失敗しました: ${reason}`,
    imported: (name) => `${name} を読み込みました`,
    importFailed: (reason) => `読み込みに失敗しました: ${reason}`,
    unknownError: "不明なエラー",
  },
};

const zh: RailSurfaceCopy = {
  map: {
    eyebrow: "§ 地图 · 站点结构",
    title: "站点地图",
    description: "查看该项目的页面顺序、路由和区块覆盖。",
    reviewSiteMap: "查看站点地图",
    activePage: "当前页面",
    emptyTitle: "尚未定义任何页面",
    emptyBody: "在聊天中描述你的应用，或从页面面板添加页面。",
    untitledPage: "未命名页面",
    blockCount: (count) => `${count} 个区块`,
    openPage: (page) => `在页面中打开 ${page}`,
  },
  code: {
    enableAllTools: "启用全部工具",
    dismiss: "关闭",
    bundleTitle: "项目捆绑包",
    bundleDescription: "导出或导入完整的 Coogo Studio 项目 JSON。",
    reviewBundle: "查看捆绑包",
    exportBundle: "导出 Coogo 捆绑包",
    exportAction: "导出",
    importBundle: "导入 Coogo 捆绑包",
    importAction: "导入",
    exported: "已导出 Coogo 捆绑包",
    exportFailed: (reason) => `导出失败：${reason}`,
    imported: (name) => `已导入 ${name}`,
    importFailed: (reason) => `导入失败：${reason}`,
    unknownError: "未知错误",
  },
};

const zhTW: RailSurfaceCopy = {
  map: {
    eyebrow: "§ 地圖 · 網站結構",
    title: "網站地圖",
    description: "檢視此專案的頁面順序、路由與區塊涵蓋。",
    reviewSiteMap: "檢視網站地圖",
    activePage: "目前頁面",
    emptyTitle: "尚未定義任何頁面",
    emptyBody: "在聊天中描述你的應用，或從頁面面板新增頁面。",
    untitledPage: "未命名頁面",
    blockCount: (count) => `${count} 個區塊`,
    openPage: (page) => `在頁面中開啟 ${page}`,
  },
  code: {
    enableAllTools: "啟用全部工具",
    dismiss: "關閉",
    bundleTitle: "專案套件包",
    bundleDescription: "匯出或匯入完整的 Coogo Studio 專案 JSON。",
    reviewBundle: "檢視套件包",
    exportBundle: "匯出 Coogo 套件包",
    exportAction: "匯出",
    importBundle: "匯入 Coogo 套件包",
    importAction: "匯入",
    exported: "已匯出 Coogo 套件包",
    exportFailed: (reason) => `匯出失敗：${reason}`,
    imported: (name) => `已匯入 ${name}`,
    importFailed: (reason) => `匯入失敗：${reason}`,
    unknownError: "未知錯誤",
  },
};

const RAIL_SURFACE_COPY: Record<LocaleCode, RailSurfaceCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getRailSurfaceCopy(locale: LocaleCode): RailSurfaceCopy {
  return RAIL_SURFACE_COPY[locale] ?? en;
}
